import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, ArrowUpRight, Loader2, AlertTriangle } from 'lucide-react';
import { SectionCard, Track } from '../design/primitives';
import { usePageHeader } from '../components/Layout';
import { useWorkspace } from '../contexts/WorkspaceContext';
import PlanBadge from '../components/PlanBadge';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function formatTRY(amountKurus) {
  if (amountKurus == null) return '—';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amountKurus / 100);
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    success: { label: 'Başarılı', color: 'var(--good)', bg: 'var(--good-weak)' },
    pending: { label: 'Bekliyor', color: 'var(--text-muted)', bg: 'var(--surface-2)' },
    failed: { label: 'Başarısız', color: 'var(--danger)', bg: 'var(--danger-weak)' },
    refunded: { label: 'İade', color: 'var(--review)', bg: 'var(--review-weak)' },
  };
  const meta = map[status] || map.pending;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px]"
      style={{ color: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
}

export default function BillingSettings() {
  const navigate = useNavigate();
  const { activeWorkspace, refresh, isWorkspaceOwner } = useWorkspace();
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [busy, setBusy] = useState(false);

  usePageHeader([{ label: 'Ayarlar', to: '/profile' }, { label: 'Faturalandırma' }], null, [activeWorkspace?.id]);

  const isOwner = isWorkspaceOwner;
  useEffect(() => {
    if (activeWorkspace && !isOwner) {
      toast.error('Bu sayfaya yalnızca workspace sahibi erişebilir.');
      navigate('/dashboard', { replace: true });
    }
  }, [activeWorkspace, isOwner, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeWorkspace || !isOwner) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [over, pays] = await Promise.all([
          axios.get(`${API}/billing/workspace/${activeWorkspace.id}`),
          axios.get(`${API}/billing/payments`, { params: { workspaceId: activeWorkspace.id } }),
        ]);
        if (!mounted) return;
        setOverview(over.data);
        setPayments(pays.data?.payments || []);
      } catch (err) {
        toast.error('Faturalandırma bilgileri yüklenemedi');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [activeWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/billing/cancel`, {
        workspaceId: activeWorkspace.id,
        immediate: cancelImmediate,
      });
      toast.success(cancelImmediate ? 'Abonelik iptal edildi' : 'Abonelik dönem sonunda iptal edilecek');
      await refresh();
      const over = await axios.get(`${API}/billing/workspace/${activeWorkspace.id}`);
      setOverview(over.data);
      setCancelOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İptal başarısız');
    } finally {
      setBusy(false);
    }
  };

  if (!activeWorkspace) {
    return (
      <div className="p-8 text-center ox-text-muted">
        Faturalandırma için bir workspace seçin.
      </div>
    );
  }

  if (!isOwner) {
    return null;
  }

  if (loading || !overview) {
    return (
      <div className="p-8 flex items-center justify-center ox-text-muted">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  const seats = overview.usage?.seats || { used: 0, limit: 0, unlimited: false };
  const projects = overview.usage?.projects || { used: 0, limit: 0, unlimited: false };
  const subscription = overview.subscription;
  const plan = overview.plan || activeWorkspace.plan || 'free';

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Plan summary */}
      <SectionCard
        title="Mevcut Plan"
        subtitle={activeWorkspace.name}
        right={
          <button onClick={() => navigate('/pricing')} className="ox-btn ox-btn-primary">
            <ArrowUpRight size={14} /><span>Planları Gör</span>
          </button>
        }
      >
        <div className="flex items-center gap-3 mb-4">
          <PlanBadge plan={plan} size="lg" />
          {subscription?.status === 'active' && (
            <span className="text-[12px] ox-text-muted">
              Sonraki yenileme: <strong style={{ color: 'var(--text)' }}>{formatDate(subscription.currentPeriodEnd)}</strong>
            </span>
          )}
          {subscription?.cancelAtPeriodEnd && (
            <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: 'var(--danger)' }}>
              <AlertTriangle size={12} /> Dönem sonunda iptal edilecek
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <div className="text-[11.5px] ox-text-muted mb-1">Kullanıcılar</div>
            <div className="flex items-baseline gap-2">
              <div className="text-[20px] font-semibold ox-tnum">{seats.used}</div>
              <div className="text-[12px] ox-text-muted">/ {seats.unlimited ? '∞' : seats.limit}</div>
            </div>
            {!seats.unlimited && (
              <div className="mt-2"><Track value={(seats.used / Math.max(1, seats.limit)) * 100} /></div>
            )}
          </div>
          <div>
            <div className="text-[11.5px] ox-text-muted mb-1">Projeler</div>
            <div className="flex items-baseline gap-2">
              <div className="text-[20px] font-semibold ox-tnum">{projects.used}</div>
              <div className="text-[12px] ox-text-muted">/ {projects.unlimited ? '∞' : projects.limit}</div>
            </div>
            {!projects.unlimited && (
              <div className="mt-2"><Track value={(projects.used / Math.max(1, projects.limit)) * 100} /></div>
            )}
          </div>
        </div>

        {subscription?.status === 'active' && !subscription.cancelAtPeriodEnd && (
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
            <button onClick={() => setCancelOpen(true)} className="ox-btn" style={{ color: 'var(--danger)' }}>
              Aboneliği İptal Et
            </button>
          </div>
        )}
      </SectionCard>

      {/* Payments history */}
      <div className="mt-6">
        <SectionCard
          title="Ödeme Geçmişi"
          subtitle={payments.length ? `${payments.length} kayıt` : 'Henüz ödeme yok'}
          pad={false}
        >
          {payments.length === 0 ? (
            <div className="p-6 text-center ox-text-muted text-[13px]">
              <CreditCard size={28} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
              Bu workspace için henüz ödeme kaydı bulunmuyor.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left px-4 py-2 ox-text-muted font-medium text-[11.5px] uppercase tracking-wider">Tarih</th>
                    <th className="text-left px-4 py-2 ox-text-muted font-medium text-[11.5px] uppercase tracking-wider">Plan</th>
                    <th className="text-left px-4 py-2 ox-text-muted font-medium text-[11.5px] uppercase tracking-wider">Tutar</th>
                    <th className="text-left px-4 py-2 ox-text-muted font-medium text-[11.5px] uppercase tracking-wider">Durum</th>
                    <th className="text-left px-4 py-2 ox-text-muted font-medium text-[11.5px] uppercase tracking-wider">Referans</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="px-4 py-2.5">{formatDate(p.createdAt)}</td>
                      <td className="px-4 py-2.5"><PlanBadge plan={p.plan} /></td>
                      <td className="px-4 py-2.5 ox-tnum">{formatTRY(p.amountKurus)}</td>
                      <td className="px-4 py-2.5"><StatusPill status={p.status} /></td>
                      <td className="px-4 py-2.5 ox-mono text-[11.5px] ox-text-muted">{p.iyzicoPaymentId || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Cancel modal */}
      {cancelOpen && (
        <div className="ox-modal-backdrop" onClick={() => setCancelOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div className="ox-surface rounded-md p-6" onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 92vw)' }}>
            <div className="text-[15px] font-semibold mb-2">Aboneliği iptal et?</div>
            <p className="text-[13px] ox-text-muted mb-4" style={{ lineHeight: 1.55 }}>
              Aboneliğinizi şimdi iptal ederseniz workspace, fazla kullanım için <strong>yalnızca okuma</strong> moduna geçer.
            </p>
            <label className="flex items-center gap-2 text-[13px] mb-4">
              <input type="checkbox" checked={cancelImmediate} onChange={(e) => setCancelImmediate(e.target.checked)} />
              <span>Hemen iptal et (aksi hâlde dönem sonunda iptal edilir)</span>
            </label>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setCancelOpen(false)} className="ox-btn">Vazgeç</button>
              <button onClick={handleCancel} disabled={busy} className="ox-btn" style={{ color: 'var(--danger)' }}>
                {busy ? <Loader2 className="animate-spin" size={14} /> : 'İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
